// SITES — where a venue the town voted for can stand (API docs/VENUE_PIPELINE_PLAN.md
// §"Sites: placed where the system sees fit").
//
// The server cannot find space on its own: the only thing that knows what is
// street, pavement and building is the road map, and the road map is
// photographed from the city model in the BROWSER (render/roadMap). So an admin
// runs this once (⚙ → Sites), it measures the city, and the candidates are
// saved as world_objects rows (kind 'site') — after which choosing one is pure
// server logic and the pipeline can run with nobody watching.
//
// Phase 1 is EXISTING, UNUSED BUILDINGS only (Khari's decision 2, 2026-10-30:
// "both, existing buildings first"): already solid for the player, already on
// the walk map, already lit. Empty lots and cloned buildings come later, which
// is why every site carries `kind`.
//
// A candidate is a building of the city model that:
//   · is ONE building — the topmost node in the model's tree whose box is
//     building-sized (the GLB groups buildings into blocks and splits them into
//     parts, and neither of those is a building)
//   · is solid in the road map (a group of stop lights at a junction has a
//     building-sized box and is mostly road underneath)
//   · has pavement along one face with a street beyond it — that face is the
//     front, and the door goes in the middle of it
//   · keeps clear of the venues, the plaza, the park, the court and the subway
// …and the survivors are spread at least `minApart` apart, best first.
//
// Pure: boxes and a map in, sites out. The three.js half (measuring the model)
// lives in components/SiteSurvey.

const ROAD = 1; const WALK = 2;   // render/roadMap classes (3 covered, 0 unknown)

// ⚠ THIS WORLD IS NOT IN METRES — a person is ~0.75 units, a city block ~7.7.
export const SITE_RULES = {
  minSide: 4, maxSide: 26,     // a shop to a long apartment block
  minArea: 28,
  minHeight: 2.6,              // a basketball court (2.2) is not a building
  maxBase: 1.0,                // standing on the ground, not a sign on a roof
  cover: 0.55,                 // share of its footprint the road map sees as covered
  walk: [0.4, 2.0],            // the pavement band in front of a face…
  road: [2.0, 7.0],            // …and the street beyond it
  minWalk: 0.7, minRoad: 0.15,
  minApart: 12,                // from a venue, and from each other
  max: 30,
};

const r2 = (n) => Math.round(n * 100) / 100;

function clsAt(map, x, z) {
  const sx = map.rect.w / map.w; const sz = map.rect.h / map.h;
  const i = Math.floor((x - map.rect.minX) / sx); const r = Math.floor((z - map.rect.minZ) / sz);
  if (i < 0 || r < 0 || i >= map.w || r >= map.h) return 0;
  return map.cls[r * map.w + i];
}
const open = (c) => c === ROAD || c === WALK;

const sized = (b, R) => {
  const w = b.maxX - b.minX; const d = b.maxZ - b.minZ; const h = b.maxY - b.minY;
  return w >= R.minSide && d >= R.minSide && w <= R.maxSide && d <= R.maxSide
    && w * d >= R.minArea && h >= R.minHeight && b.minY <= R.maxBase;
};

/**
 * The buildings among a model's nodes: [{ name, parent, minX..maxZ }] in tree
 * order (a parent before its children) → the topmost building-sized ones.
 */
export function pickBuildings(nodes, R = SITE_RULES) {
  const taken = new Array(nodes.length).fill(false);   // this node or an ancestor is a building
  const out = [];
  nodes.forEach((n, i) => {
    const up = n.parent >= 0 ? taken[n.parent] : false;
    if (up) { taken[i] = true; return; }
    if (n.name && isFinite(n.minX) && sized(n, R)) { taken[i] = true; out.push(n); }
  });
  return out;
}

/** Share of the box (shrunk by 15% a side) the road map calls covered. */
function coverage(map, b) {
  const w = b.maxX - b.minX; const d = b.maxZ - b.minZ;
  const x0 = b.minX + w * 0.15; const x1 = b.maxX - w * 0.15;
  const z0 = b.minZ + d * 0.15; const z1 = b.maxZ - d * 0.15;
  const step = 0.5; let n = 0; let c = 0;
  for (let x = x0; x <= x1; x += step) for (let z = z0; z <= z1; z += step) { n++; if (clsAt(map, x, z) === 3) c++; }
  return n ? c / n : 0;
}

// The four faces of an axis-aligned box, as a facing (local +z = the street).
const FACES = [0, Math.PI / 2, Math.PI, -Math.PI / 2];

/** How one face of a box meets the street: its pavement and road shares, and its door. */
function face(map, b, rotY, R) {
  const cx = (b.minX + b.maxX) / 2; const cz = (b.minZ + b.maxZ) / 2;
  const nx = Math.round(Math.sin(rotY)); const nz = Math.round(Math.cos(rotY));
  const lx = Math.round(Math.cos(rotY)); const lz = Math.round(-Math.sin(rotY));
  const sizeX = b.maxX - b.minX; const sizeZ = b.maxZ - b.minZ;
  const w = nx ? sizeZ : sizeX; const d = nx ? sizeX : sizeZ;
  const edge = d / 2;                    // the box's face, from the centre
  let walk = 0; let road = 0; let nW = 0; let nR = 0;
  for (let t = -0.3; t <= 0.3001; t += 0.05) {
    const ox = cx + lx * t * w; const oz = cz + lz * t * w;
    for (let o = R.walk[0]; o <= R.walk[1]; o += 0.3) { nW++; if (open(clsAt(map, ox + nx * (edge + o), oz + nz * (edge + o)))) walk++; }
    for (let o = R.road[0]; o <= R.road[1]; o += 0.5) { nR++; if (clsAt(map, ox + nx * (edge + o), oz + nz * (edge + o)) === ROAD) road++; }
  }
  const walkFrac = nW ? walk / nW : 0; const roadFrac = nR ? road / nR : 0;
  // THE DOOR. Middle of the face if the pavement in front of it is clear, else
  // the nearest clear spot along it (a lamp post, a bin). Set on the face the
  // MAP sees — the first open ground walking out from the middle — rather than
  // on the box, which is pushed out by awnings and signs.
  let door = null;
  for (const t of [0, 0.12, -0.12, 0.24, -0.24, 0.34, -0.34]) {
    const ox = cx + lx * t * w; const oz = cz + lz * t * w;
    let e = null;
    for (let o = Math.max(0, edge - 3); o <= edge + 2; o += 0.15) {
      if (open(clsAt(map, ox + nx * o, oz + nz * o))) { e = o; break; }
    }
    if (e == null) continue;
    let clear = true;
    for (let o = 0.3; o <= 1.5; o += 0.2) if (!open(clsAt(map, ox + nx * (e + o), oz + nz * (e + o)))) { clear = false; break; }
    if (clear) { door = [r2(ox + nx * e), r2(oz + nz * e)]; break; }
  }
  return { rotY, w, d, walkFrac, roadFrac, door };
}

/**
 * Survey a city. `buildings` from pickBuildings; `map` a road map ({ cls, w, h,
 * rect }); `anchors` [[x, z], …] the venues standing now; `keepOut` { rects:
 * [[x0, x1, z0, z1], …], points: [[x, z, r], …] }. Returns { sites, rejected },
 * sites best first: { ref, x, z, rotY, w, d, door, score, kind }.
 */
export function surveySites({ buildings, map, anchors = [], keepOut = {}, rules = {} }) {
  const R = { ...SITE_RULES, ...rules };
  const rejected = { solid: 0, frontage: 0, door: 0, venue: 0, zone: 0, spacing: 0 };
  const found = [];
  for (const b of buildings || []) {
    const cx = (b.minX + b.maxX) / 2; const cz = (b.minZ + b.maxZ) / 2;
    if (anchors.some((a) => Math.hypot(a[0] - cx, a[1] - cz) < R.minApart)) { rejected.venue++; continue; }
    const inRect = (keepOut.rects || []).some(([x0, x1, z0, z1]) => b.maxX + 2 >= x0 && b.minX - 2 <= x1 && b.maxZ + 2 >= z0 && b.minZ - 2 <= z1);
    const nearPoint = (keepOut.points || []).some(([x, z, r]) => Math.hypot(x - cx, z - cz) < r);
    if (inRect || nearPoint) { rejected.zone++; continue; }
    if (coverage(map, b) < R.cover) { rejected.solid++; continue; }
    // The best face: most pavement, then most street beyond it.
    let best = null;
    for (const th of FACES) {
      const f = face(map, b, th, R);
      if (f.walkFrac < R.minWalk || f.roadFrac < R.minRoad) continue;
      const s = f.walkFrac + 0.6 * f.roadFrac;
      if (!best || s > best.s) best = { ...f, s };
    }
    if (!best) { rejected.frontage++; continue; }
    if (!best.door) { rejected.door++; continue; }
    const score = best.s + (best.w >= 6 ? 0.15 : 0) + Math.min(0.15, (best.w * best.d) / 600);
    found.push({
      ref: b.name, kind: 'building',
      x: r2(cx), z: r2(cz), rotY: r2(best.rotY), w: r2(best.w), d: r2(best.d),
      door: best.door, score: r2(score),
    });
  }
  // Spread out, best first: a site too near a better one is dropped.
  found.sort((p, q) => q.score - p.score || (p.ref < q.ref ? -1 : 1));
  const sites = [];
  for (const s of found) {
    if (sites.length >= R.max) break;
    if (sites.some((t) => Math.hypot(t.x - s.x, t.z - s.z) < R.minApart)) { rejected.spacing++; continue; }
    sites.push(s);
  }
  return { sites, rejected };
}

/** A site as the world_objects row the API's site chooser reads. */
export function siteRow(s) {
  return {
    zone_id: 'street', kind: 'site', ref: s.ref,
    pos: [s.x, 0, s.z], rotY: s.rotY, size: 1,
    data: { kind: s.kind || 'building', w: s.w, d: s.d, door: s.door },
  };
}
